import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import {
  DEFAULT_PENDING_REQUEST_FILTERS,
  PENDING_REQUESTS_PAGE_SIZE,
  PendingRequestFilters,
  PendingRequestOrderBy,
  PendingRequestRow,
  PendingRequestsEndpoint,
  PendingRequestsResponse,
} from './pending-requests-types';

export const usePendingActivityRequests = (endpoint: PendingRequestsEndpoint) => {
  const apiUrl = useApiUrl();
  const listFetch = useCampfireFetch<PendingRequestsResponse>({ defer: true });
  const downloadFetch = useCampfireFetch<PendingRequestsResponse>({ defer: true });

  const [filters, setFiltersState] = useState<PendingRequestFilters>(DEFAULT_PENDING_REQUEST_FILTERS);
  const [orderBy, setOrderBy] = useState<PendingRequestOrderBy>('name');
  const [orderAscending, setOrderAscending] = useState(true);
  const [page, setPage] = useState(0);
  const [refetchToken, setRefetchToken] = useState(0);
  const [data, setData] = useState<PendingRequestsResponse>();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const buildBody = (offset: number, limit: number, download: boolean) => ({
    programIds: filters.programIds,
    activityIds: filters.activityIds,
    query: filters.query,
    ...(filters.dateFrom && {
      dateFrom: DateTime.fromISO(filters.dateFrom)
        .startOf('day')
        .toISO(),
    }),
    ...(filters.dateTo && {
      dateTo: DateTime.fromISO(filters.dateTo)
        .endOf('day')
        .toISO(),
    }),
    orderBy,
    orderDirection: orderAscending,
    offset,
    limit,
    download,
  });

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError(null);

    listFetch
      .run({
        url: `${apiUrl}/vm/management/${endpoint}`,
        method: 'post',
        data: buildBody(page * PENDING_REQUESTS_PAGE_SIZE, PENDING_REQUESTS_PAGE_SIZE, false),
      })
      .then((response) => {
        if (requestId !== latestRequest.current) return;
        if (!response.ok || !response.data?.data) {
          setError('Unable to load data. Please try again.');
          setLoading(false);
          return;
        }
        const result = response.data.data;
        if (result.items.length === 0 && page > 0) {
          setPage(page - 1);
          return;
        }
        setData(result);
        setLoading(false);
      })
      .catch(() => {
        if (requestId !== latestRequest.current) return;
        setError('Unable to load data. Please try again.');
        setLoading(false);
      });
  }, [filters, orderBy, orderAscending, page, refetchToken]);

  const setFilters = (changes: Partial<PendingRequestFilters>) => {
    setFiltersState((current) => ({ ...current, ...changes }));
    setPage(0);
  };

  const resetFilters = () => {
    setFiltersState(DEFAULT_PENDING_REQUEST_FILTERS);
    setPage(0);
  };

  const toggleSort = (field: PendingRequestOrderBy) => {
    if (field === orderBy) {
      setOrderAscending(!orderAscending);
    } else {
      setOrderBy(field);
      setOrderAscending(field !== 'date');
    }
    setPage(0);
  };

  const refetch = () => setRefetchToken((token) => token + 1);

  const fetchAllRows = (): Promise<PendingRequestRow[]> => {
    setDownloading(true);
    return downloadFetch
      .run({
        url: `${apiUrl}/vm/management/${endpoint}`,
        method: 'post',
        data: buildBody(0, PENDING_REQUESTS_PAGE_SIZE, true),
      })
      .then((response) => {
        if (!response.ok || !response.data?.data) {
          throw new Error('Unable to download data');
        }
        return response.data.data.items;
      })
      .finally(() => setDownloading(false));
  };

  return {
    rows: data?.items ?? [],
    total: data?.total ?? 0,
    filterOptions: data?.filterOptions ?? { programs: [], activities: [] },
    loading,
    downloading,
    error,
    filters,
    setFilters,
    resetFilters,
    orderBy,
    orderAscending,
    toggleSort,
    page,
    setPage,
    refetch,
    fetchAllRows,
  };
};
